import { Clock4, LucideCalendarDays, Radio } from "lucide-react";
import React, { useState } from "react";
import EventModal from "../../../../components/ui/EventModal";

const EventCard = ({ event, displayNumber }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getHeaderTitle = () => {
    if (event.category === "webinar") {
      return `Webinar #${displayNumber}`;
    } else if (event.category === "competitions") {
      return `Competitions #${displayNumber}`;
    }
    return `Event #${displayNumber}`;
  };

  const handleViewMore = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleRegisterNow = () => {
    if (event.registerLink && event.status !== "happening-now") {
      window.open(event.registerLink, "_blank");
    }
  };

  const isHappeningNow = event.status === "happening-now";
  const isDisplayOnly = event.status === "displayOnly";
  const isRegisterDisabled = isHappeningNow || !event.registerLink;

  return (
    <div className="w-full h-full bg-white overflow-hidden flex flex-col items-start drop-shadow-md relative">
      {/* Header Banner */}
      <div className="bg-Color-Primary/70 py-2 px-3 text-white rounded-t-xl">
        <h3 className="text-lg font-semibold">{getHeaderTitle()}</h3>
      </div>

      <div className="w-full flex-1 rounded-tr-2xl rounded-b-2xl overflow-hidden shadow-lg flex flex-col">
        <div children="">
          {/* Background Image Section - Renewable Energy Theme */}
          <div
            className="h-48 w-full  relative overflow-hidden"
            style={{
              backgroundImage: `linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #1d4ed8 100%)`,
            }}
          >
            <img
              src={event.image}
              alt={event.title}
              className="w-full h-full object-cover opacity-80"
            />
            {/* <div className='absolute inset-0 bg-black bg-opacity-20'></div> */}

            {/* Happening Now Button - Only on Banner Image */}
            {isHappeningNow && (
              <>
                <style jsx>{`
                  @keyframes colorPulse {
                    0%,
                    100% {
                      background-color: #dc2626;
                    }
                    50% {
                      background-color: #f87171;
                    }
                  }
                  .color-pulse {
                    animation: colorPulse 3s ease-in-out infinite;
                  }
                `}</style>
                <button className="absolute flex flex-row items-center gap-1 bottom-2 right-2 text-white px-2 py-1 rounded-full font-bold text-xs shadow-lg hover:bg-red-400 transition-colors duration-200 z-10 color-pulse">
                  <Radio className="size-4" />
                  <p>HAPPENING NOW</p>
                </button>
              </>
            )}
          </div>
          {/* Title */}
          <div className="p-4 pb-2">
            <h2 className="text-xl font-semibold text-gray-900 mb-3 leading-tight w-full">
              {event.title}
            </h2>

            {/* Registration Count - Only show if registrationCount exists */}
            {event.registrationCount && (
              <p className="text-Color-Black text-base mb-4">
                {event.registrationCount} people registered in this event
              </p>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="p-4 pt-0 flex-1 flex flex-col bg-Color-White">
          {/* Event Details */}
          <div className="space-y-3 mb-6 flex-1">
            {/* Brief */}
            {event.brief && (
              <div className="text-Color-Black">
                <p className="text-base">{event.brief}</p>
              </div>
            )}
            
            {/* Date */}
            {event.date && (
              <div className="flex items-center text-Color-Black">
                <LucideCalendarDays className="size-5 text-gray-700 mr-2" />
                <span className="text-base font-medium">{event.date}</span>
              </div>
            )}

            {/* Time */}
            {event.time && (
              <div className="flex items-center text-Color-Black">
                <Clock4 className="size-5 text-gray-700 mr-2" />

                <span className="text-base font-medium">{event.time}</span>
              </div>
            )}

            {/* Platform */}
            {event.platform && (
              <div className="flex items-center text-Color-Black">
                <Radio className="size-5 text-gray-700 mr-2" />

                <span className="text-base font-medium">{event.platform}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-auto">
            <button
              onClick={handleViewMore}
              className={`${isDisplayOnly ? 'w-full' : 'flex-1'} bg-transparent border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 text-sm`}
            >
              VIEW MORE
            </button>
            {!isDisplayOnly && (
              <button
                onClick={handleRegisterNow}
                disabled={isRegisterDisabled}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors duration-200 text-sm ${
                  isRegisterDisabled
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-Color-Brand-Red text-white hover:bg-red-900"
                }`}
              >
                {isHappeningNow ? "CLOSED" : (!event.registerLink ? "OPEN SOON!" : "REGISTER NOW")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Event Modal */}
      <EventModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        event={event}
        isUpcoming={true}
        onRegister={handleRegisterNow}
      />
    </div>
  );
};

export default EventCard;
